
import { useState, useCallback } from 'react';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { useToast } from '@/hooks/use-toast';
import { PostgrestError } from '@supabase/supabase-js';

// --- Type Definitions (should be in a shared file eventually) ---
import { RuleCondition } from '@/components/RuleConditionRow';
import { availableFields } from '@/utils/ruleDefinitions';
import { canonicalFieldKey } from '@/utils/ruleFormat';

interface ConditionGroup {
  id?: string;
  combinator: 'AND' | 'OR'; // New: How conditions within this group are combined
  conditions: RuleCondition[];
}

type ActionType = 'assign_offer';

export interface RuleAction {
  id?: string;
  type: ActionType;
  value: { offer_template_id: string };
}

export interface OfferRule {
  id?: string;
  name: string;
  description: string;
  priority: number;
  is_active: boolean;
  tags: string[];
  group_combinator: 'AND' | 'OR'; // New: How condition groups are combined
  condition_groups: ConditionGroup[];
  actions: RuleAction[];
  json_representation: object;
}

export function normalizeRule(rule: any): OfferRule {
  return {
    ...rule,
    group_combinator: rule.group_combinator || 'AND',
    condition_groups: (rule.condition_groups ?? []).map((group: any) => ({
      ...group,
      combinator: group.combinator || 'AND',
      // The update RPC persists field keys with underscores; the builder and the
      // plain-language formatters both key off the dotted form, so bring stored
      // keys back to the canonical spelling on read.
      conditions: (group.conditions ?? []).map((condition: RuleCondition) => ({
        ...condition,
        field:
          typeof condition.field === 'string'
            ? canonicalFieldKey(condition.field, availableFields)
            : condition.field,
      })),
    })),
    actions: rule.actions ?? [],
  };
}

interface SanitizedRuleCondition {
  field: string;
  operator: string;
  value: any;
}

interface SanitizedConditionGroup {
  combinator: 'AND' | 'OR';
  conditions: SanitizedRuleCondition[];
}

interface SanitizedRuleAction {
  type: ActionType;
  value: { offer_template_id: string };
}

interface SanitizedOfferRule {
  name: string;
  description: string;
  priority: number;
  is_active: boolean;
  tags: string[];
  group_combinator: 'AND' | 'OR';
  json_representation: object;
  condition_groups: SanitizedConditionGroup[];
  actions: SanitizedRuleAction[];
}

// Utility to sanitize rule before sending to Supabase
function sanitizeRuleForUpdate(rule: OfferRule): SanitizedOfferRule {
  return {
    name: rule.name,
    description: rule.description,
    priority: Number(rule.priority),
    is_active: Boolean(rule.is_active),
    tags: Array.isArray(rule.tags) ? rule.tags : [],
    group_combinator: rule.group_combinator === 'AND' || rule.group_combinator === 'OR' ? rule.group_combinator : 'AND',
    json_representation: rule.json_representation,
    condition_groups: rule.condition_groups.map(group => ({
      combinator: group.combinator === 'AND' || group.combinator === 'OR' ? group.combinator : 'AND',
      conditions: group.conditions.map(cond => ({
        // The backend function expects field names with underscores instead of dots.
        field: typeof cond.field === 'string' ? cond.field.replace(/\./g, '_') : cond.field,
        operator: cond.operator,
        value: cond.value,
      })),
    })),
    actions: rule.actions.map(action => ({
      type: action.type || 'assign_offer',
      value: action.value,
    })),
  };
}

export const useOfferRules = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const [rules, setRules] = useState<OfferRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOfferRule = async (rule: Omit<OfferRule, 'id'>) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    setLoading(true);
    try {
      // Use a Supabase RPC function to handle the transaction
      const { data, error } = await supabase.rpc('create_full_offer_rule', { 
        rule_data: rule 
      });

      if (error) throw error;

      toast({ title: "Success", description: "Offer rule created successfully." });
      fetchOfferRules(); // Refresh the list
      return data;

    } catch (err: any) {
      setError(err.message);
      toast({ title: "Error", description: `Failed to create rule: ${err.message}`, variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchOfferRules = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('offer_rules')
        .select('*, condition_groups:offer_rule_condition_groups(*, conditions:offer_rule_conditions(*)), actions:offer_rule_actions(*)')
        .order('priority', { ascending: true });

      if (error) throw error;
      setRules((data as OfferRule[]).map(normalizeRule));

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient]);

  const updateOfferRule = async (rule: OfferRule) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    setLoading(true);
    try {
      const sanitizedRule = sanitizeRuleForUpdate(rule);
      const { data, error } = await supabase.rpc('update_full_offer_rule', {
        rule_id: rule.id,
        rule_data: sanitizedRule
      });

      if (error) throw error;

      toast({ title: "Success", description: "Offer rule updated successfully." });
      fetchOfferRules(); // Refresh the list
      return data;

    } catch (err: any) {
      setError(err.message);
      toast({ title: "Error", description: `Failed to update rule: ${err.message}`, variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteOfferRule = async (ruleId: string) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    setLoading(true);
    try {
      const { error } = await supabase.rpc('delete_full_offer_rule', {
        rule_id: ruleId
      });

      if (error) throw error;

      toast({ title: "Success", description: "Offer rule deleted successfully." });
      fetchOfferRules(); // Refresh the list

    } catch (err: any) {
      setError(err.message);
      toast({ title: "Error", description: `Failed to delete rule: ${err.message}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return { rules, loading, error, fetchOfferRules, createOfferRule, updateOfferRule, deleteOfferRule };
};